import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import { getAPIURL, setCachedBusiness, getCachedBusiness } from "../lib/utils";
import { refreshToken, withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";
import UserContext from "./user-context";
import { prefix } from "../lib/auth";
import { set } from "date-fns";
import Loading from "../components/ui/loading";

const BusinessContext = createContext({
  businessList: null,
  activeBusiness: null,
  fetchBusinessList: () => { },
  setActiveBusiness: () => { },
  fetchStaffList: () => { },
  staffList: null,
  setStaffList: () => { },
  teammates: null,
  activeStaffs: null,
  pendingStaffs: null,
  setNotificationContent: () => { },
  notificationContent: {
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  },
  userHasAdminAccess: false,
  currentUser: null,
  isGuestUser: false,
  pegIsInstalled: null,
  pegCheckReady: null,
  createDeveloper: () => { },
  isDeveloperRegistered: null
});

export const BusinessProvider = (props) => {
  const [businessList, setBusinessList] = useState(null);
  const [activeBusiness, setBusiness] = useState(null);
  const [staffList, setStaffList] = useState(null);
  const { user } = useContext(UserContext);
  const [notificationContent, setNotificationContent] = useState({
    showNotification: false,
    header: '',
    message: '',
    icon: null,
  });
  const router = useRouter();
  const currentUser = staffList ? staffList.find(staff => staff.user.email === user?.email) : null;
  const isGuestUser = currentUser?.role === 'guest';
  const activeStaffs = staffList ? staffList.filter(staff => staff.user.email !== '' && staff.role !== 'owner') : null;
  const pendingStaffs = staffList ? staffList.filter(staff => staff.user.email === '') : null;
  const [installedPegs, setInstalledPegs] = useState(null);
  const [pegIsInstalled, setPegIsInstalled] = useState(null);
  const [pegCheckReady, setPegCheckReady] = useState(false);
  const [isDeveloperRegistered, setIsDeveloperRegistered] = useState(null);
  const userHasAdminAccess = staffList
    ? staffList.some(
      staff =>
        staff.user.email === user?.email &&
        (staff.role === "admin" || staff.role === "owner")
    )
    : false;

  const fetchBusinessList = useCallback(() => {
    return withActiveToken(() => {
      fetch(getAPIURL("businesses/"), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            if (json.length === 0) {
              router.push("/businesses/new");
              return;
            }

            setBusinessList(json);

            const businessUuid = router.query.uuid;

            if (businessUuid) {
              const activeBusiness = json.find(
                (business) => business.uuid == businessUuid
              );

              if (activeBusiness) {
                setActiveBusiness(activeBusiness);
                return;
              }
            }

            const cachedBusiness = getCachedBusiness();

            const activeBusiness = cachedBusiness
              ? json.find((business) => business.uuid == cachedBusiness) ||
              json[0]
              : json[0];
            setActiveBusiness(activeBusiness);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    }, router);
  }, [router]);

  const fetchStaffList = useCallback((callBack) => {
    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/staff/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            callBack && callBack(json);
            setStaffList(json);
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }
  );

  const createDeveloper = async (payload, callBack) => {
    
    withActiveToken(async () => {
      try {
        const response = await fetch(`https://repo.workpeg.com/api/developers/`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-BUSINESS': activeBusiness.uuid,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        if (!response.ok) throw new Error('Failed to create developer')
        const data = await response.json()
        callBack && callBack(data)
        setIsDeveloperRegistered(true);
      } catch (error) {
        console.error(error)
      }
    })

  }
  const getDevelopers = async (callBack) => {
    
    withActiveToken(async () => {
      try {
        const response = await fetch(`https://repo.workpeg.com/api/developers/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-BUSINESS': activeBusiness.uuid,
            'Content-Type': 'application/json'
          },
        })
        if (!response.ok) throw new Error('Failed to fetch developers')
        const data = await response.json()
        callBack && callBack(data)
        if (data.length > 0) {
          setIsDeveloperRegistered(true);
        } else {
          setIsDeveloperRegistered(false);
          router.push(`/${activeBusiness?.uuid}/onboarding`);
        }
      } catch (error) {
        console.error(error)
      }
    })

  }

  const fetchDevelopers = () => {
    refreshToken(
      () => {getDevelopers() }
    )
  }

  const installPeg = (peg) => {
    const payload = {
      "urn": `${peg}:1.0`,
      "skip_setup": true,
    }

    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/pegs/`), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            setInstalledPegs((prev) => [...prev, json]);
            const names = [...(installedPegs?.map(peg => peg.peg.name) ?? []), json.peg.name];
            localStorage.setItem(`${getCachedBusiness()}_installed_pegs`, JSON.stringify(names));
          });
        }
        return res.json().then((json) => Promise.reject(json));
      });
    });
  }

  const fetchInstalledPegs = () => {

    return withActiveToken(() => {
      fetch(getAPIURL(`businesses/${activeBusiness?.uuid}/pegs/`), {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }).then((res) => {
        if (res.ok) {
          return res.json().then((json) => {
            const names = json.map(peg => peg.peg.name);
            const installed = json.some(peg => peg.peg.name === 'developers');

            setInstalledPegs(json);
            setPegIsInstalled(installed);
            localStorage.setItem(`${getCachedBusiness()}_installed_pegs`, JSON.stringify(names));
            setPegCheckReady(true);

            if (!installed) {
              installPeg('developers');
            }
          });
        }
        return res.json().then((json) => {
          setPegCheckReady(true);
          return Promise.reject(json);
        });
      });
    }, router);
  };


  useEffect(() => {
    if (businessList == null && router.isReady) {
      fetchBusinessList();
    }
  }, [businessList, fetchBusinessList, router.isReady, activeBusiness]);

  useEffect(() => {
    if (activeBusiness) {
      setPegCheckReady(false);
      setInstalledPegs(null);
      fetchStaffList();
      fetchInstalledPegs();
      fetchDevelopers();
    }
  }, [activeBusiness]);

  function setActiveBusiness(business) {
    setBusiness(business);
    setCachedBusiness(business.uuid);
  }
  // Add the updateBusinessList function
  function updateBusinessList(newList) {
    setBusinessList(newList); // Update the business list state
  }
  const teammates = staffList ? staffList.filter(staff => staff.user.first_name) : null;

  const context = {
    businessList,
    activeBusiness,
    fetchBusinessList,
    setActiveBusiness,
    updateBusinessList,
    fetchStaffList,
    staffList,
    setStaffList,
    teammates,
    activeStaffs,
    pendingStaffs,
    notificationContent,
    setNotificationContent,
    userHasAdminAccess,
    currentUser,
    isGuestUser,
    pegIsInstalled,
    pegCheckReady,
    createDeveloper,
    isDeveloperRegistered,
  };

  return (
    <BusinessContext.Provider value={context}>
      {isDeveloperRegistered === null
      ? <Loading />
      : props.children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext;
